import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Coins, ArrowRight } from 'lucide-react';
import type { TopUpPack } from '../../services/walletService';
import Card from '../ui/Card';
import NoticeBar from '../ui/NoticeBar';
import { useWallet } from '../../contexts/WalletContext';
import { trackStarterPackOfferShown, trackStarterPackOfferClicked } from '../../services/analyticsService';

/**
 * In-stream top-up card — rendered when an agent turn hits "out of credits". Shows the
 * recommended pack and links to the wallet. Monochrome, minimal. The service (which pulls
 * firebase) is lazy-loaded so this stays test-friendly when rendered inside the chat stream.
 */
/**
 * Lightweight "out of credits" reminder for the CHAT STREAM — the same slim nudge BAR as
 * RetentionNudge (icon + message + "See plans"), deliberately unobtrusive instead of a big central
 * card (the heavier TopUpCard is reserved for settings panels). Gentle: the user can keep chatting.
 */
export const TopUpNotice: React.FC = () => {
  // TRY-4: an account that has never bought credits is offered the starter pack first — the cheapest way in.
  const { starterPack } = useWallet();
  const offered = !!starterPack;
  useEffect(() => { if (offered) trackStarterPackOfferShown('topup_notice'); }, [offered]);
  return (
  <NoticeBar icon={<Coins className="w-4 h-4" />} data-testid="topup-notice">
    <p className="flex-1 text-sm text-secondary-700 dark:text-secondary-300 leading-snug">
      {starterPack
        ? `This needs credits. Start with ${starterPack.credits.toLocaleString()} for $${starterPack.price_usd}, enough to animate and publish your first episode. Credits never expire.`
        : 'You’re out of credits for now — top up to keep going. Purchased credits never expire.'}
    </p>
    <Link
      to="/wallet"
      onClick={() => { if (offered) trackStarterPackOfferClicked('topup_notice'); }}
      className="shrink-0 rounded-lg bg-secondary-900 dark:bg-white px-3 py-1.5 text-xs font-medium text-white dark:text-secondary-900 hover:opacity-90 transition-opacity"
    >
      {starterPack ? 'Get the starter pack' : 'See plans'}
    </Link>
  </NoticeBar>
  );
};

const TopUpCard: React.FC = () => {
  const [packs, setPacks] = useState<TopUpPack[]>([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { getTopUpInfo } = await import('../../services/walletService');
        const info = await getTopUpInfo();
        if (alive) setPacks(info.packs);
      } catch { /* page still works */ }
    })();
    return () => { alive = false; };
  }, []);
  // TRY-4: the starter pack leads for an account that has never bought credits (the server decides eligibility).
  const { starterPack } = useWallet();
  const offered = !!starterPack;
  useEffect(() => { if (offered) trackStarterPackOfferShown('topup_card'); }, [offered]);
  const best = starterPack || packs.find((p) => p.tag) || packs[0];

  return (
    <Card padding="lg" data-testid="topup-card" className="w-full max-w-sm">
      <div className="flex items-start gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary-100 dark:bg-secondary-800">
          <Coins className="h-4 w-4 text-secondary-600 dark:text-secondary-300" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold leading-tight text-secondary-900 dark:text-white">{starterPack ? 'Start with the starter pack' : 'Top up your credits'}</p>
          <p className="mt-1 text-xs leading-relaxed text-secondary-500">
            {best
              ? <>{best.credits.toLocaleString()} WP for ${best.price_usd}{best.bonus_pct ? ` · +${best.bonus_pct}% bonus` : ''}</>
              : 'Keep the conversation going.'}
          </p>
        </div>
      </div>
      <Link
        to="/wallet"
        onClick={() => { if (offered) trackStarterPackOfferClicked('topup_card'); }}
        className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-xl bg-secondary-900 dark:bg-white px-3 py-2.5 text-sm font-medium text-white dark:text-secondary-900 hover:bg-black dark:hover:bg-secondary-100 transition-colors"
      >
        See top-up plans <ArrowRight className="h-3.5 w-3.5" />
      </Link>
    </Card>
  );
};

export default TopUpCard;
